import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OButton from 'components/reusable/OButton';
import { apiPost } from 'utils/apiFetch';
import apiPath from 'utils/apiPath';
import useToastContext from 'hooks/useToastContext';

/**
 * MFASetupModal
 *
 * Drop this into wherever Empivo's security-settings screen lives
 * (I don't have that file, so this ships as a standalone modal you can
 * open from a "Enable MFA" button anywhere in settings).
 *
 * Steps: intro -> QR scan + confirm code -> recovery codes -> done.
 * Mirrors ConfirmationModal's panel/overlay styling.
 */
const STEP = { INTRO: 'intro', SCAN: 'scan', RECOVERY: 'recovery' };

const MFASetupModal = ({ isOpen, onClose, onEnabled, loginSetup, onLoginConfirm }) => {
  const { t } = useTranslation();
  const notification = useToastContext();

  const [step, setStep] = useState(STEP.INTRO);
  const [isLoading, setIsLoading] = useState(false);
  const [setupData, setSetupData] = useState(null); // { qrCodeDataUrl, manualEntryKey }
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    if (loginSetup?.qrCodeDataUrl) {
      setSetupData({
        qrCodeDataUrl: loginSetup.qrCodeDataUrl,
        manualEntryKey: loginSetup.manualEntryKey,
      });
      setStep(STEP.SCAN);
      setCode('');
      setError('');
    }
  }, [isOpen, loginSetup?.qrCodeDataUrl, loginSetup?.manualEntryKey]);

  if (!isOpen) return null;

  const reset = () => {
    setStep(STEP.INTRO);
    setSetupData(null);
    setCode('');
    setError('');
    setRecoveryCodes([]);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const startSetup = async () => {
    setIsLoading(true);
    try {
      const { data } = await apiPost(apiPath.mfaSetupInitiate, {});
      if (data?.success) {
        setSetupData(data.data);
        setStep(STEP.SCAN);
      } else {
        notification.error(data?.message || 'Failed to start MFA setup.');
      }
    } catch (err) {
      notification.error(err?.response?.data?.message || 'Failed to start MFA setup.');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmSetup = async (e) => {
    e?.preventDefault?.();
    if (isLoading) return;
    if (code.replace(/\D/g, '').length !== 6) {
      setError(t('MFA_ENTER_CODE') || 'Please enter the 6-digit code.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      if (onLoginConfirm) {
        const result = await onLoginConfirm(code);
        if (result?.verified) {
          setRecoveryCodes(result.recoveryCodes || []);
          setStep(STEP.RECOVERY);
          notification.success('MFA enabled. Save your recovery codes.');
        } else {
          setError(result?.message || 'Invalid code. Please try again.');
        }
        return;
      }
      const { data } = await apiPost(apiPath.mfaSetupVerify, { token: code });
      if (data?.success) {
        setRecoveryCodes(data.data.recoveryCodes || []);
        setStep(STEP.RECOVERY);
        notification.success(data.message || 'MFA enabled.');
      } else {
        setError(data?.message || 'Invalid code. Please try again.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const finish = () => {
    onEnabled?.();
    handleClose();
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard?.writeText(recoveryCodes.join('\n'));
    notification.success(t('MFA_CODES_COPIED') || 'Recovery codes copied.');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={handleClose}></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {step === STEP.INTRO && !loginSetup && (
              <div className="text-center sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {t('MFA_SETUP_TITLE') || 'Set up two-factor authentication'}
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {t('MFA_SETUP_INTRO') ||
                    "You'll need an authenticator app like Google Authenticator or Microsoft Authenticator on your phone."}
                </p>
              </div>
            )}

            {step === STEP.SCAN && setupData && (
              <div className="text-center sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {t('MFA_SCAN_TITLE') || 'Scan this QR code'}
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {loginSetup
                    ? 'This country is high-risk. Do not scan with the phone camera. Open Google Authenticator or Microsoft Authenticator, then scan this QR or type the key below.'
                    : t('MFA_SCAN_DESC') ||
                      'Do not scan with the phone camera. Open Google Authenticator or Microsoft Authenticator, then scan this QR or type the key below.'}
                </p>
                <div className="flex justify-center my-4">
                  <img
                    src={setupData.qrCodeDataUrl}
                    alt="MFA QR code"
                    className="w-44 h-44 border-2 border-[#DFDFDF] rounded-lg p-2"
                  />
                </div>
                <div className="text-center bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700 mb-1">
                    No QR? In the authenticator app tap Add account → Enter a setup key, then paste this key:
                  </p>
                  <p className="font-mono text-base break-all text-gray-900">
                    {setupData.manualEntryKey}
                  </p>
                  <button
                    type="button"
                    className="mt-2 text-sm text-[#6236FF] hover:underline"
                    onClick={() => {
                      navigator.clipboard?.writeText(setupData.manualEntryKey || '');
                      notification.success('Setup key copied. Paste it in the authenticator app.');
                    }}
                  >
                    Copy setup key
                  </button>
                </div>
                <form onSubmit={confirmSetup} className="mt-4">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setError('');
                    }}
                    disabled={isLoading}
                    placeholder="000000"
                    className="block w-full text-center tracking-[0.4em] text-lg py-3 px-3 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 focus:border-blue-500"
                  />
                  {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
                </form>
              </div>
            )}

            {step === STEP.RECOVERY && (
              <div className="text-center sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {t('MFA_RECOVERY_TITLE') || 'Save your recovery codes'}
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {t('MFA_RECOVERY_INTRO') ||
                    "Store these somewhere safe. Each code can be used once if you lose access to your authenticator app. They won't be shown again."}
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2 bg-gray-50 rounded-lg p-4 font-mono text-sm">
                  {recoveryCodes.map((c) => (
                    <span key={c}>{c}</span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={copyRecoveryCodes}
                  className="mt-3 text-sm text-[#6236FF] hover:text-[#9D36FF] hover:underline"
                >
                  {t('MFA_COPY_CODES') || 'Copy codes'}
                </button>
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {step === STEP.INTRO && !loginSetup && (
              <>
                <OButton
                  onClick={startSetup}
                  label={t('MFA_CONTINUE') || 'Continue'}
                  isLoading={isLoading}
                  extraClasses="ml-3 bg-LightBlue hover:bg-DarkBlue"
                />
                <OButton onClick={handleClose} label={t('CANCEL') || 'Cancel'} variant="outline" />
              </>
            )}
            {step === STEP.SCAN && (
              <>
                <OButton
                  onClick={confirmSetup}
                  label={t('MFA_VERIFY_AND_ENABLE') || 'Verify & enable'}
                  isLoading={isLoading}
                  extraClasses="ml-3 bg-LightBlue hover:bg-DarkBlue"
                />
                <OButton onClick={handleClose} label={t('CANCEL') || 'Cancel'} variant="outline" disabled={isLoading} />
              </>
            )}
            {step === STEP.RECOVERY && (
              <OButton onClick={finish} label={t('MFA_DONE') || 'Done'} extraClasses="ml-3 bg-LightBlue hover:bg-DarkBlue" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFASetupModal;
